import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import { filter, Subscription, tap } from 'rxjs';
import { ContractDataService } from '../../services/contract-data.service';
import { IContractDataModel, IContractRight, INewStep, IRoadMapHistory } from '../../models/contract-internal-models';
import { TranslateService } from '@ngx-translate/core';
import { AppContractStepTargetSelectComponent } from '../contract-step-target-select/contract-step-target-select.component';
import { MatDialog } from '@angular/material/dialog';
import { ContractGraphService } from '../../services/contract-graph.service';
import { IDivStyles, STEPS_STYLING_MAP } from '../../models/contract-styling-consts';
import { ContractTagsService } from '../../services/contract-tags.service';
import { ContractContextMenuService } from '../../services/contract-context-menu.service';
import { ContractFormService } from '../../services/contract-form.service';
import { ContractActionsMainService } from '../../services/contract-actions-main.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ContractRestrictionService } from '../../services/contract-restrictions.service';
import { ContractRoadmapModel, ContractStepModel } from '../../../../models/contract.model';
import { GraphKnDataService } from '../../../graph-konva/services/graph-kn.data.service';
import { IStepNameData } from '../../../../services/contracts/contract-control.service';

@Component({
    selector: 'app-contract-roadmap',
    templateUrl: './contract-roadmap.component.html',
    styleUrl: './contract-roadmap.component.scss',
    changeDetection:ChangeDetectionStrategy.OnPush,
 
})
export class AppContractRoadmapComponent {
  @Input() contractGuid: string='';
  public isContractInterDomain:boolean = false
  private contractData:IContractDataModel|undefined = undefined
  public contractRoadmap: ContractRoadmapModel|null=null;
  public contractStatus: string='closed';
  public contactRights: IContractRight|null=null;
  @ViewChildren('stepsDivs') stepsDivs! :QueryList<ElementRef>
  public stepsStylingMap = STEPS_STYLING_MAP
  private subs:Subscription = new Subscription()
  public selectedContractStep:ContractStepModel|null = null;
  public selectedContractStepDivData!:IDivStyles;
  constructor(
    public contractDataService:ContractDataService,
    public contractRestrictionService:ContractRestrictionService,
    private contractActionsMainService: ContractActionsMainService,
    private contextMenuService: ContractContextMenuService,
    private contractTagsService:ContractTagsService,
    private contractFormService:ContractFormService,
    private knDataService:GraphKnDataService,
    private contractGraphService:ContractGraphService,
    private translateService:TranslateService,
    private dialog:MatDialog,
    private cds:ChangeDetectorRef,
  ) {  }

  ngOnInit(): void {
    this.setSubscriptions()
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe()
  }
  setSubscriptions() {
    //targetData loaded 
    this.subs.add(this.contractDataService.subStepDataChanged.asObservable().subscribe(()=>this.cds.detectChanges()))
    this.subs.add(this.contractDataService.bSubContractDataIsCached.asObservable()
    .pipe(filter(data=>data.guid===this.contractGuid))
    .subscribe(()=>{
      this.contractData = this.contractDataService.ContractDataCachedByGuid.get(this.contractGuid);
      this.isContractInterDomain = this.contractData?.contract?.contract? this.contractFormService.checkContractDomainIsCurrent(this.contractData?.contract?.contract) : false
      if (this.contractData) {
        this.contractRoadmap = this.contractData.contractRoadmap
        this.contactRights = this.contractData.rights
      }
      this.contractStatus = this.contractData?.contract?.contract.status||'closed'
      this.cds.detectChanges()
    }))
    //add step
    this.subs.add(this.contractDataService.subContractStepCreateNew.asObservable().subscribe(data=>this.addNewStepHandler(data)))
    //open or close step
    this.subs.add(this.contractDataService.subContractStepToOpenOne.asObservable()
    .pipe(filter(data=>data.contractGuid === this.contractGuid))
    .subscribe(data=>{
      this.selectedContractStep = data.contractStep
      this.cds.detectChanges()
      }))
    //delete step + contextMenu
    this.subs.add(
      this.contextMenuService.subDeleteContextMenu.asObservable()
      .pipe(filter(data=>data.targetContext==='roadMapStep'))
      .subscribe(data=>this.deleteStep(data.data as ContractStepModel)))
    //save step
    this.subs.add(
      this.contractDataService.subContractModifiedSaveOrCancel
      .subscribe(toSave=>this.contractActionsMainService.saveContract(toSave,this.contractGuid,this.contractStatus, this.contractRoadmap)))
    //roadmap order has been changed by graph component
    this.subs.add(this.setOnNewStepsOrderChanged());
    //step name changed
    this.subs.add(
      this.contractDataService.subStepDataChanged.asObservable().subscribe(data=>{
        this.stepNameChangedHandler(data)
        let roadMap = (this.contractRoadmap as ContractRoadmapModel) 
        let steps = roadMap.steps
        let step =  roadMap.steps? roadMap?.steps?.find(el=>el.guid===data.step.guid) : null
        step? step.tagData = this.contractTagsService.updateStepTags(data.step,this.contractStatus, this.contractDataService.currentMember!,this.contractGuid) : null
      }))
    //roadMap history list is opened
    this.subs.add(this.contractDataService.subRoadMapHistoryListOpened.asObservable().subscribe(this.selectedContractStep = null))
    //roadMap changed from history
    this.subs.add(this.contractDataService.subRoadMapSelected.asObservable()
      .pipe(filter(data=>data && (data as IRoadMapHistory).label>=''))
      .subscribe(data=>this.cds.detectChanges()))
  }
  roadMapChanged(dragEvent:CdkDragDrop<ContractStepModel[]>){
    if (this.contractRoadmap?.steps)  {
      moveItemInArray(this.contractRoadmap.steps, dragEvent.previousIndex, dragEvent.currentIndex);
      this.contractRoadmap.steps.forEach((step,index)=>step.sort_index = index)
      this.contractDataService.subContractEdited.next(true);
    }
}
setOnNewStepsOrderChanged(): Subscription {
    return this.knDataService.subOnStepsOrderChanged$.asObservable().subscribe(newOrderSteps => {
        if (this.contractRoadmap?.steps) {
            this.contractRoadmap?.steps.forEach(step => step.sort_index = newOrderSteps.find(el => el.guid === step.guid)?.sort_index)
            this.contractRoadmap.steps.sort((a, b) => ((a.sort_index || 0) - (b.sort_index || 0)))
            this.contractDataService.subContractEdited.next(true);
            this.cds.detectChanges()
      }
    })
  }
  addTarget(step:ContractStepModel) {
    this.contractFormService.checkInterDomainRedirect(this.contractData?.contract?.contract).subscribe(()=>{
      let stepSelectModal = this.dialog.open(AppContractStepTargetSelectComponent,{
              minWidth: '80vw',
              maxHeight: '97vh',
              panelClass: 'project-select-padding-modalbox'
      })
      stepSelectModal.componentInstance.roadmap = this.contractRoadmap?.steps!
      stepSelectModal.componentInstance.contractGuid = this.contractGuid||''
      stepSelectModal.componentInstance.step = step 
      stepSelectModal.componentInstance.dialogToClose = stepSelectModal
    })
  }
  stepNameChangedHandler(data:IStepNameData) {
    let step = this.contractRoadmap?.steps?.find(el=>el.guid===data.step.guid)
    if (!step) {
      return
    }
    step?.auto_name? step.auto_name = data.stepName : Object.assign(step,{auto_name:data.stepName});
    this.cds.detectChanges()
  }
  addNewStepHandler(data:INewStep){
    data.stepType==="credit_letter"? data.stepType='payment' : null;
    switch (data.action) {
        case 'preView':
          let stepPreview = new ContractStepModel();
          stepPreview.target_type = data.stepType;
          stepPreview.name = null;
          stepPreview.target = {};
          stepPreview.auto_name = this.translateService.instant('CONTRACTS.CARD.STEPS.' + data.stepType),
          stepPreview.target.is_verified = false;
          stepPreview.sort_index = this.contractRoadmap?.steps?.length||0;
          stepPreview.guid = 'Preview';
          if (data.stepType && ['payment','direct_payment'].includes(data.stepType)) {
              stepPreview.payment_data = {
                  total: 0,
                  payer_guid: null,
                  recipient_guid: null,
              };
          }
          if (data.stepType === 'multi_payment') {
              stepPreview.multi_payment_data = []
          };
          this.contractRoadmap?.steps?.push(stepPreview)
          this.contractGraphService.prepareGraph(true,true,this.contractGuid,{},this.contractRoadmap?.steps);
          let scroll= document.getElementById('scroll')
          this.contractDataService.isGraphShown? null : scroll?.scrollIntoView({block: "end",behavior: 'smooth'});
        break;
        case 'none':
          if (this.contractRoadmap?.steps && this.contractRoadmap?.steps?.find(el=>el.guid === 'Preview')) {
            this.contractRoadmap.steps = this.contractRoadmap?.steps?.filter(el=>el.guid!=='Preview')
            this.contractGraphService.prepareGraph(true,true,this.contractGuid,{},this.contractRoadmap.steps);
          }
        break;
        case 'add':
            let newStep = this.contractRoadmap?.steps?.find(el=>el.guid==='Preview')
            newStep? newStep.guid = 'Preview_' + newStep.sort_index : null;
            newStep? this.contractDataService.subContractEdited.next(true) : null;
            this.contractDataService.subContractStepCreateNew.next({action:'preView',stepType:data.stepType})
        break;
    }
    this.cds.detectChanges()
  }    
  deleteStep(stepToDelete:ContractStepModel) {
    this.contractFormService.checkInterDomainRedirect(this.contractData?.contract?.contract).subscribe(()=>{
      this.contractDataService.subContractEdited.next(true)
      this.contractRoadmap?.steps? 
          this.contractRoadmap.steps = this.contractRoadmap?.steps?.filter(el=>el.guid!==stepToDelete.guid):null;
      this.selectedContractStep = null;
      this.contractDataService.subContractStepToOpenOne.next({contractGuid:this.contractGuid,contractStep:null})
    })
  }
  openStepForm (contractStep:ContractStepModel){
    if (this.selectedContractStep?.guid ===contractStep.guid) {
      this.selectedContractStep = null;
      this.contractDataService.subContractStepToOpenOne.next({contractGuid:this.contractGuid,contractStep:null})
      return
    } 
    this.contractFormService.showHistoryList = false
    let stepDiv = this.stepsDivs.find(el=>el.nativeElement?.id===contractStep.guid)
    
    this.selectedContractStep = contractStep
    
    this.selectedContractStepDivData = {
      top:stepDiv?.nativeElement.offsetTop,
      height:stepDiv?.nativeElement.offsetHeight,
      stepIsCalculated:false
    }
    this.contractDataService.subContractStepToOpenOne.next({
      contractGuid:this.contractGuid,
      stepDivData:this.selectedContractStepDivData,
      contractStep:this.selectedContractStep
    })
  }
}